import type { ComponentProps, PropsWithChildren } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  background: var(--background);
  clip-path: polygon(
    50% 0,
    100% 0,
    100% calc(100% - var(--bezel-size)),
    calc(100% - var(--bezel-size)) 100%,
    0 100%,
    0 var(--bezel-size),
    var(--bezel-size) 0,
    50% 0,
    0 0
  );
  will-change: clip-path;
  box-shadow: var(--box-shadow);
  @supports (clip-path: shape(from 0 0, close)) {
    &:not(.sharp) {
      clip-path: shape(
        from 0 0,
        hline to calc(100% - var(--radius)),
        arc to 100% var(--radius) of var(--radius) cw,
        vline to calc(100% - var(--bezel-size) - var(--radius) / 2),
        arc to calc(100% - var(--radius) / 4)
          calc(100% - var(--bezel-size) + var(--radius) / 4) of var(--radius) cw,
        line to calc(100% - var(--bezel-size) + var(--radius) / 4)
          calc(100% - var(--radius) / 4),
        arc to calc(100% - var(--bezel-size) - var(--radius) / 2) 100% of
          var(--radius) cw,
        hline to var(--radius),
        arc to 0 calc(100% - var(--radius)) of var(--radius) cw,
        vline to calc(var(--bezel-size) + var(--radius) / 2),
        arc to calc(var(--radius) / 4)
          calc(var(--bezel-size) - var(--radius) / 4) of var(--radius) cw,
        line to calc(var(--bezel-size) - var(--radius) / 4)
          calc(var(--radius) / 4),
        arc to calc(var(--bezel-size) + var(--radius) / 2) 0 of var(--radius) cw,
        close
      );
    }
  }
`;
const Border = styled.div`
  position: absolute;
  inset: 0;
  background: var(--border-color);
  clip-path: polygon(
    evenodd,
    50% 0,
    50% var(--border),
    calc(100% - var(--border)) var(--border),
    calc(100% - var(--border)) calc(100% - var(--border) - var(--bezel-size)),
    calc(100% - var(--border) - var(--bezel-size)) calc(100% - var(--border)),
    var(--border) calc(100% - var(--border)),
    var(--border) calc(var(--border) + var(--bezel-size)),
    calc(var(--border) + var(--bezel-size)) var(--border),
    50% var(--border),
    50% 0,
    100% 0,
    100% calc(100% - var(--bezel-size)),
    calc(100% - var(--bezel-size)) 100%,
    0 100%,
    0 calc(var(--bezel-size)),
    calc(var(--bezel-size)) 0,
    50% 0
  );
  @supports (clip-path: shape(from 0 0, close)) {
    &:not(.sharp &) {
      clip-path: shape(
        evenodd from 0 0,
        hline to 50%,
        vline to var(--border),
        hline to calc(100% - var(--radius)),
        arc to calc(100% - var(--border)) calc(var(--radius)) of
          calc(var(--radius) - var(--border)) cw,
        vline to
          calc(100% - var(--bezel-size) - var(--border) - var(--radius) / 4),
        arc to calc(100% - var(--radius) / 6 - var(--border))
          calc(100% - var(--bezel-size) + var(--radius) / 4 - var(--border)) of
          var(--radius) cw,
        line to
          calc(100% - var(--bezel-size) - var(--border) + var(--radius) / 4)
          calc(100% - var(--radius) / 6 - var(--border)),
        arc to
          calc(100% - var(--bezel-size) - var(--radius) / 6 - var(--border))
          calc(100% - var(--border)) of var(--radius) cw,
        hline to var(--radius),
        arc to var(--border) calc(100% - var(--radius)) of
          calc(var(--radius) - var(--border)) cw,
        vline to calc(var(--bezel-size) + var(--border) + var(--radius) / 4),
        arc to calc(var(--radius) / 6 + var(--border))
          calc(var(--bezel-size) - var(--radius) / 4 + var(--border)) of
          var(--radius) cw,
        line to calc(var(--bezel-size) + var(--border) - var(--radius) / 4)
          calc(var(--border) + var(--radius) / 6),
        arc to calc(var(--bezel-size) + var(--radius) / 4 + var(--border))
          var(--border) of var(--radius) cw,
        hline to 50%,
        vline to 0,
        hline to calc(100% - var(--radius)),
        arc to 100% calc(var(--radius)) of calc(var(--radius)) cw,
        vline to calc(100% - var(--bezel-size) - var(--radius) / 4),
        arc to calc(100% - var(--radius) / 6)
          calc(100% - var(--bezel-size) + var(--radius) / 4) of var(--radius) cw,
        line to calc(100% - var(--bezel-size) + var(--radius) / 4)
          calc(100% - var(--radius) / 6),
        arc to calc(100% - var(--bezel-size) - var(--radius) / 6) 100% of
          var(--radius) cw,
        hline to var(--radius),
        arc to 0 calc(100% - var(--radius)) of calc(var(--radius)) cw,
        vline to calc(var(--bezel-size) + var(--radius) / 4),
        arc to calc(var(--radius) / 6)
          calc(var(--bezel-size) - var(--radius) / 4) of var(--radius) cw,
        line to calc(var(--bezel-size) - var(--radius) / 4)
          calc(var(--radius) / 6),
        arc to calc(var(--bezel-size) + var(--radius) / 4) 0 of var(--radius) cw,
        close
      );
    }
  }
`;

type Props = {
  background?: string;
  bezelSize?: number;
  radius?: number;
  borderWidth?: number;
  borderColor?: string;
  boxShadow?: string;
  className?: string;
  style?: CSSProperties;
} & ComponentProps<typeof Root>;
export const Bezel = ({
  children,
  background = 'var(--c-nd-100)',
  bezelSize = 16,
  radius = 8,
  borderWidth = 1,
  borderColor = 'var(--c-nd-100)',
  boxShadow,
  className,
  style,
  ...rest
}: PropsWithChildren<Props>) => {
  return (
    <Root
      style={
        {
          ...style,
          '--background': background,
          '--bezel-size': `${bezelSize}px`,
          '--radius': `${radius}px`,
          '--border': `${borderWidth}px`,
          '--border-color': borderColor,
          '--box-shadow': boxShadow,
        } as CSSProperties
      }
      className={className}
      {...rest}
    >
      {children}
      <Border />
    </Root>
  );
};
